
import React, { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Sparkles, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { usePageSEO } from "@/hooks/use-seo";
import { supabase } from "@/integrations/supabase/client";
import { US_STATES } from "@/constants/usStates";
import PublicHeader from "@/components/layout/PublicHeader";
import { useToast } from "@/hooks/use-toast";
import { useThrottle } from "@/hooks/use-throttle";
import { useQuery } from "@tanstack/react-query";
import { SpeciesAutocomplete } from "@/components/ui/species-autocomplete";
import { normalizeSpecies, canonicalSpeciesSet } from "@/lib/speciesNormalization";

const LegalLookup: React.FC = () => {
  usePageSEO("Legal Lookup – Zoocertica", "Research state-by-state exotic animal laws.", "/legal-lookup");
  const { toast } = useToast();
  const { isThrottled, remainingTime, executeAction } = useThrottle('legal-lookup', 20000);
  
  // AI Lookup state
  const [aiAnimal, setAiAnimal] = useState("");
  const [aiState, setAiState] = useState("");
  const [aiResult, setAiResult] = useState<any>(null);
  const [aiLoading, setAiLoading] = useState(false);

  // Fetch unique species from legal_rules for autocomplete
  const { data: speciesSuggestions = [] } = useQuery({
    queryKey: ['legal-species-suggestions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('legal_rules')
        .select('species');
      
      if (error) throw error;
      
      // Combine DB species + canonical species from map (using Set for O(1) dedup)
      const combined = new Set([
        ...data.map(d => d.species),
        ...Array.from(canonicalSpeciesSet)
      ]);
      
      return Array.from(combined).sort();
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const handleAiLookup = () => {
    if (isThrottled) {
      toast({
        title: "Rate limit active",
        description: `Please wait ${remainingTime} seconds before making another lookup to prevent abuse.`,
        variant: "destructive"
      });
      return;
    }

    if (!aiAnimal.trim() || !aiState) {
      toast({
        title: "Missing information",
        description: "Please enter an animal name and select a state.",
        variant: "destructive"
      });
      return;
    }

    executeAction(async () => {
      setAiLoading(true);
      setAiResult(null);

      // Normalize species ONCE before sending to backend
      const normalizedAnimal = normalizeSpecies(aiAnimal.trim());

      try {
        // Guests allowed; the client attaches a session token automatically if signed in
        const { data, error } = await supabase.functions.invoke('ai-legal-lookup', {
          body: { animal: normalizedAnimal, state: aiState },
        });

        if (error) {
          console.error('AI lookup error:', error);
          if (error.message?.includes('Rate limit')) {
            toast({
              title: "Rate limit exceeded",
              description: error.message,
              variant: "destructive"
            });
          } else if (error.message?.includes('Unauthorized')) {
            toast({
              title: "Authentication required",
              description: "Please log in again to use this feature.",
              variant: "destructive"
            });
          } else {
            toast({
              title: "AI lookup failed",
              description: "Please try again later.",
              variant: "destructive"
            });
          }
          return;
        }

        setAiResult(data);
      } catch (error) {
        console.error('AI lookup error:', error);
        toast({
          title: "An unexpected error occurred",
          description: "Please try again later.",
          variant: "destructive"
        });
      } finally {
        setAiLoading(false);
      }
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <div className="container mx-auto py-6 space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">Legal Lookup</h1>
          <p className="text-muted-foreground">
            Research state-by-state exotic animal laws and regulations
          </p>
        </div>


        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              AI Legal Lookup
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Animal Name</label>
              <SpeciesAutocomplete
                value={aiAnimal}
                onChange={setAiAnimal}
                suggestions={speciesSuggestions}
                placeholder="e.g., Fennec Fox, Capybara, Tiger"
              />
            </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">State</label>
                <Select value={aiState} onValueChange={setAiState}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((state) => (
                      <SelectItem key={state.value} value={state.value}>
                        {state.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button 
              onClick={handleAiLookup} 
              disabled={aiLoading || isThrottled}
              className="w-full"
            >
              {aiLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Looking up...
                </>
              ) : isThrottled ? (
                `Wait ${remainingTime}s`
              ) : (
                "Lookup"
              )}
            </Button>
            
            {isThrottled && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Rate limit active. You can make another lookup in {remainingTime} seconds. This prevents abuse of our AI service.
                </AlertDescription>
              </Alert>
            )}
            
            {aiResult && (
              <div className="mt-6 p-4 bg-muted/50 rounded-lg space-y-3">
                <h3 className="font-semibold">AI Legal Summary</h3>
                <p className="text-sm">{aiResult.summary}</p>
                
                {aiResult.requirements && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Requirements:</h4>
                    <Table>
                      <TableBody>
                        {Object.entries(aiResult.requirements).map(([key, value]) => (
                          <TableRow key={key}>
                            <TableCell className="font-medium py-2">{key}</TableCell>
                            <TableCell className="py-2">{value as string}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                
                <div className="text-xs text-muted-foreground border-t pt-3">
                  ⚠️ This is AI-generated information. Always verify with official agencies before making decisions.
                </div>
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default LegalLookup;
